import React from 'react';
import Header from '../../components/header/header';
import { Link } from 'react-router-dom';
import './visit.scss';
import { motion } from "motion/react";

const Visit = () => {
  return (
    <div className="visit">
      <Header />

      {/* Hero Section */}
      <section className="hero7">
        <div className="hero-content7">
          <h1>Plan Your Visit</h1>
          <p>We can't wait to meet you! Here's everything you need to know for your first visit.</p>
        </div>
      </section>

      {/* Service Times Section - Stagger from bottom */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="section7"
      >
        <div className="section-header7">
          <h2 className="section-title7">Service Times</h2>
          <p className="section-subtitle7">Join us every Sunday for dynamic worship and biblical teaching</p>
        </div>

        <div className="info-grid">
          {[
            { icon: "📖", title: "Sunday School", time: "10: 30 AM", desc: "A dedicated time for studying scripture, deepening understanding, and nurturing spiritual growth together." },
            { icon: "⛪", title: "Sunday Service", time: "11:00 AM", desc: "A time of collective worship, heartfelt praise, prayer, and spiritual renewal in God’s presence." },
            { icon: "💻", title: "Online Service", time: "11:00 AM", desc: "Can't make it in person? Join our livestream every Sunday from anywhere in the world." }
          ].map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="info-card"
            >
              <div className="info-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <div className="info-highlight">{service.time}</div>
              <p>{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* What to Expect Section - Slide from right */}
      <motion.section
        initial={{ opacity: 0, x: 60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="section7 visit-add"
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className="section-header7 color-v">
            <h2 className="section-title7 color-v">What to Expect</h2>
            <p className="section-subtitle7 color-v">Your first visit can feel overwhelming. Here's what happens when you arrive.</p>
          </div>

          <div className="expect-grid">
            <div className="expect-content">
              <ul className="expect-list">
                {[
                  { icon: "👋", title: "Warm Welcome", desc: "Our hospitality team will greet you at the door and help you find your way around." },
                  // { icon: "☕", title: "Coffee & Fellowship", desc: "Grab a complimentary coffee or tea in our lobby before service begins." },
                  { icon: "👨‍👩‍👧‍👦", title: "Children's Check-In", desc: "Safe, fun, and age-appropriate programs for infants through 5th grade during both services." },
                  { icon: "🎵", title: "Worship & Message", desc: "Enjoy 20-25 minutes of worship followed by a practical, biblical message (about 35-40 minutes)." },
                  { icon: "👔", title: "Come As You Are", desc: "Dress comfortably! You'll see everything from jeans to suits. We care about your heart, not your clothes." }
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <span className="list-icon">{item.icon}</span>
                    <div className="list-content">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="expect-image"
            >
              <img src="https://res.cloudinary.com/dsnontai6/image/upload/v1770567384/0A3A3621_xqbpns.jpg" alt="Church Service" />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Parking & Accessibility - Scale in */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
        className="section7"
      >
        <div className="section-header7">
          <h2 className="section-title7">Parking & Accessibility</h2>
        </div>

        <div className="info-grid">
          {[
            { icon: "🚗", title: "Parking", desc: "Free parking is available in our main lot with additional overflow parking across the street. Handicap accessible spots are located near the main entrance." },
            { icon: "♿", title: "Accessibility", desc: "Our facility is fully wheelchair accessible with ramps. ASL interpretation is available upon request with advance notice." },
            // { icon: "🚸", title: "Family Friendly", desc: "Nursing rooms, family restrooms, and a cry room with livestream viewing are available for parents with young children." }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="info-card"
            >
              <div className="info-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Location Section - Slide from bottom */}
      <motion.section
        initial={{ opacity: 0, y: 70 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="location-section"
      >
        <div className="location-content">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="location-info"
          >
            <h2>Find Us</h2>
            <div className="location-details">
              <div className="location-item">
                <span className="location-item-icon">📍</span>
                <div className="location-item-content">
                  <h4>Address</h4>
                  <p>211 E Main St,<br />Middletown, <br />DE 19709.</p>
                </div>
              </div>
              <div className="location-item">
                <span className="location-item-icon">📞</span>
                <div className="location-item-content">
                  <h4>Phone</h4>
                  <p>(302) 465-0760</p>
                </div>
              </div>
              <div className="location-item">
                <span className="location-item-icon">✉️</span>
                <div className="location-item-content">
                  <h4>Email</h4>
                  <p><a href="mailto:pr@rccgagcde.com">pr@rccgagcde.com</a></p>
                </div>
              </div>
            </div>
            <Link to="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="location-button">Get Directions</Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="location-map"
          >
            <iframe 
              src="https://www.google.com/maps?q=211+E+Main+St,+Middletown,+DE+19709&output=embed" 
              width="100%" 
              height="400" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="Church Location">
            </iframe>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Visit;
